import { nextTick, onScopeDispose, ref, watch } from 'vue'

// The live tracker of each element across all useTracking instances, so a
// container nested in another (a delayed section on a CMS page) never counts
// its promotions twice
const trackerOf = new WeakMap()

const _destroy = (trackings) => {
  trackings.forEach((t) => {
    if (trackerOf.get(t.$el) === t) trackerOf.delete(t.$el)
    t.destroy()
  })
}

// auto: re-run tracking whenever the element or ga4Data changes. Components
// that drive initPromotionAndContentTracking() themselves opt out — a deep
// watch on ga4Data would re-init on every child mutation.
const useTracking = (el, { auto = true } = {}) => {
  const state = {
    promotions: [],
    contents: []
  }
  const ga4Data = ref({})
  // Bumped by every kill, so a run that is still loading knows it was
  // superseded and neither claims elements nor keeps its trackers
  let run = 0
  const kill = () => {
    run++
    _destroy(state.promotions)
    _destroy(state.contents)
  }
  // Vue stops the component's watchers before it clears the template ref on
  // unmount, so the element watcher never sees null
  onScopeDispose(kill, true)

  const track = async (type, key, current) => {
    const { [type]: trackType } = await _getModules()
    if (current !== run) return

    const trackings = _claim(el, ga4Data, trackType)
    state[key] = trackings
    await Promise.all(trackings.map((t) => t.init()))
    // A kill during init destroyed them before init attached its observers
    if (current !== run) _destroy(trackings)
  }

  const initPromotionAndContentTracking = async () => {
    kill()
    const current = run
    await nextTick()

    setTimeout(() => {
      // prevent failing on <!--v-if--> when there is no rendered content
      if (!el?.value?.tagName) return

      void track('Promotion', 'promotions', current)
      void track('Content', 'contents', current)
    }, 100)
  }

  if (auto) {
    watch(el, initPromotionAndContentTracking)
    watch(ga4Data, initPromotionAndContentTracking, { deep: true })
  }

  return { ga4Data, kill, initPromotionAndContentTracking }
}

const _claim = (el, data, trackType) => {
  // The element can unmount while the tracking modules are still loading
  if (!el.value?.tagName) return []

  return [
    ...el.value.querySelectorAll(`[data-${trackType.getAttributeName()}]`)
  ]
    .filter((e) => !trackerOf.has(e))
    .map((e) => {
      const tracking = new trackType(e, data)
      trackerOf.set(e, tracking)
      return tracking
    })
}

const _getModules = (() => {
  let cache

  return async () => {
    if (cache) return cache

    const [{ Promotion }, { Content }] = await Promise.all([
      import('@/services/analytics/Promotion'),
      import('@/services/analytics/Content')
    ])
    cache = { Promotion, Content }
    return cache
  }
})()

export { useTracking }
